import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authAPI } from '@/services/api'
import { debugError } from '@/utils/env'

export interface User {
  user_id: number
  email: string
  is_email_verified: boolean
}

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const verificationSent = ref(false)
  const authToken = ref<string | null>(null)

  // 发送验证码
  const sendVerificationCode = async (email: string): Promise<boolean> => {
    try {
      isLoading.value = true
      
      const response: any = await authAPI.sendVerification(email)
      
      // 检查业务层错误码
      if (response && typeof response === 'object' && response.errorcode !== '0') {
        throw new Error(response.errormsg || '发送验证码失败')
      }
      
      // 临时存储邮箱信息
      user.value = {
        user_id: 0,
        email,
        is_email_verified: false
      }
      
      verificationSent.value = true
      return true
    } catch (error) {
      debugError('发送验证码失败:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // 验证邮箱
  const verifyEmail = async (code: string): Promise<boolean> => {
    try {
      if (!user.value?.email) {
        throw new Error('请先发送验证码')
      }

      isLoading.value = true

      const response: any = await authAPI.verifyEmail(user.value.email, code)

      // 检查业务层错误码
      if (response && typeof response === 'object' && response.errorcode !== '0') {
        throw new Error(response.errormsg || '验证失败')
      }

      // response.data 包含 { message, user, token }
      const data = response.data || response
      if (data.user) {
        // 验证成功，更新用户信息
        user.value = data.user

        // 保存后端返回的 JWT token
        if (data.token) {
          authToken.value = data.token
          localStorage.setItem('auth_token', data.token)
        }

        // 持久化用户信息
        localStorage.setItem('user', JSON.stringify(user.value))
      }

      return true
    } catch (error) {
      debugError('验证邮箱失败:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // 重新发送验证码
  const resendVerificationCode = async (): Promise<boolean> => {
    if (!user.value?.email) {
      throw new Error('请先输入邮箱')
    }
    
    verificationSent.value = false
    return await sendVerificationCode(user.value.email)
  }

  // 获取当前用户信息
  const fetchCurrentUser = async (): Promise<void> => {
    try {
      const response: any = await authAPI.getCurrentUser()
      if (response.user) {
        user.value = response.user
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    } catch (error) {
      debugError('获取用户信息失败:', error)
      // 如果token无效，清除本地数据
      logout()
    }
  }

  // 登出
  const logout = () => {
    user.value = null
    verificationSent.value = false
    authToken.value = null
    localStorage.removeItem('user')
    localStorage.removeItem('auth_token')
  }

  // 初始化用户状态
  const initUser = () => {
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('user')
    console.log('savedToken', savedToken)
    console.log('savedUser', savedUser)
    if (savedToken && savedUser) {
      try {
        authToken.value = savedToken
        user.value = JSON.parse(savedUser)
        // 验证token有效性
        fetchCurrentUser()
      } catch (error) {
        debugError('解析用户信息失败:', error)
        logout()
      }
    }
  }

  // 响应式属性：检查是否已验证（用于模板响应式渲染）
  const isVerified = computed(() => {
    return user.value?.is_email_verified || false
  })

  // 响应式属性：获取用户邮箱（用于模板响应式渲染）
  const userEmail = computed(() => {
    return user.value?.email || ''
  })

  // 检查是否已登录
  const isLoggedIn = computed(() => {
    return !!(user.value && authToken.value)
  })

  return {
    user,
    isLoading,
    verificationSent,
    authToken,
    isVerified,
    userEmail,
    isLoggedIn,
    sendVerificationCode,
    verifyEmail,
    resendVerificationCode,
    fetchCurrentUser,
    logout,
    initUser
  }
})